import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import TomSelect from 'tom-select';

import { FinanceService, FinanceTaxRecord } from '../../../core/services/finance.service';

@Component({
  selector: 'app-tax-selector',
  standalone: false,
  templateUrl: './tax-selector.html',
  styleUrl: './tax-selector.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TaxSelector),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TaxSelector),
      multi: true,
    },
  ],
})
export class TaxSelector implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {
  @ViewChild('selectEl') selectEl!: ElementRef<HTMLSelectElement>;

  @Input() taxes: FinanceTaxRecord[] = [];
  @Input() placeholder = 'Taxes';
  @Input() disabled = false;
  @Input() scope: 'purchase' | 'sales' | 'both' = 'purchase';
  @Input() defaultToFirst = false;
  @Input() defaultTaxIds: string[] = [];
  @Input() showManageLink = true;
  @Input() selectClass = 'form-select';

  @Output() taxesLoaded = new EventEmitter<FinanceTaxRecord[]>();

  private tom: TomSelect | null = null;
  private allTaxOptions: FinanceTaxRecord[] = [];
  private taxOptions: FinanceTaxRecord[] = [];
  private value: string[] = [];
  private internalDisabled = false;
  private missingTaxIds: string[] = [];

  constructor(private readonly financeService: FinanceService) {}

  async ngOnInit(): Promise<void> {
    await this.loadTaxes();
    await this.ensureDefaultSelection();
    this.refreshOptions();
  }

  ngAfterViewInit(): void {
    this.initTom();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taxes'] || changes['scope']) {
      if (changes['taxes']) {
        this.allTaxOptions = this.taxes;
      }
      this.taxOptions = this.filterTaxes(this.allTaxOptions);
      void this.ensureDefaultSelection().then(() => this.refreshOptions());
      return;
    }

    if (changes['defaultTaxIds'] || changes['defaultToFirst']) {
      void this.ensureDefaultSelection().then(() => this.refreshOptions());
      return;
    }

    if (changes['manageLink'] || changes['showManageLink']) {
      this.refreshOptions();
    }
  }

  ngOnDestroy(): void {
    this.tom?.destroy();
    this.tom = null;
  }

  writeValue(value: unknown): void {
    this.value = Array.isArray(value)
      ? value.map((item) => String(item || '').trim()).filter(Boolean)
      : [];
    this.syncMissingTaxIds();
    this.tom?.setValue(this.value, true);
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.internalDisabled = isDisabled;
    this.syncDisabledState();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.missingTaxIds.length === 0) {
      return null;
    }

    return {
      taxPreload: {
        message: 'Unable to preload selected taxes.',
        missing: this.missingTaxIds,
      },
    };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private async loadTaxes(): Promise<void> {
    if (this.taxes.length > 0) {
      this.allTaxOptions = this.taxes;
      this.taxOptions = this.filterTaxes(this.allTaxOptions);
      return;
    }

    try {
      const taxes = await this.financeService.getTaxes({ include_inactive: false });
      this.allTaxOptions = taxes;
      this.taxOptions = this.filterTaxes(this.allTaxOptions);
      this.taxesLoaded.emit(this.taxOptions);
    } catch {
      this.allTaxOptions = [];
      this.taxOptions = [];
    }
  }

  private async ensureDefaultSelection(): Promise<void> {
    if (!this.defaultToFirst || this.value.length > 0) {
      return;
    }

    let defaultIds = this.defaultTaxIds
      .map((taxId) => String(taxId || '').trim())
      .filter(Boolean);

    if (defaultIds.length === 0) {
      try {
        defaultIds = await this.financeService.getDefaultTaxIds(this.scope);
      } catch {
        defaultIds = [];
      }
    }

    const availableTaxIds = new Set(this.taxOptions.map((tax) => tax.id));
    defaultIds = defaultIds.filter((taxId) => availableTaxIds.has(taxId));
    if (defaultIds.length === 0 && this.taxOptions.length > 0) {
      defaultIds = [this.taxOptions[0].id];
    }

    if (defaultIds.length === 0) {
      return;
    }

    this.value = defaultIds;
    this.syncMissingTaxIds();
    this.tom?.setValue(this.value, true);
    this.onChange(this.value);
    this.onValidatorChange();
  }

  private initTom(): void {
    this.tom = new TomSelect(this.selectEl.nativeElement, {
      wrapperClass: 'ts-wrapper app-tom-select-shell',
      controlClass: 'ts-control app-tom-select-control',
      plugins: ['remove_button'],
      maxItems: null,
      valueField: 'value',
      labelField: 'text',
      searchField: ['text'],
      placeholder: this.placeholder,
      dropdownParent: 'body',
      options: this.toTomOptions(),
      render: {
        item: (data: any, escape: (value: string) => string) =>
          `<div class="app-input-pill"><span class="app-input-pill__label">${escape(data.text)}</span></div>`,
        option: (data: any, escape: (value: string) => string) =>
          `<div>${escape(data.text)}</div>`,
      },
      onChange: (value: unknown) => {
        this.value = Array.isArray(value)
          ? value.map((item) => String(item))
          : String(value || '').split(',').filter(Boolean);
        this.syncMissingTaxIds();
        this.onChange(this.value);
        this.onValidatorChange();
      },
      onBlur: () => this.onTouched(),
    });
    this.tom.setValue(this.value, true);
    this.syncDisabledState();
  }

  private refreshOptions(): void {
    this.syncMissingTaxIds();
    if (!this.tom) {
      this.onValidatorChange();
      return;
    }

    this.tom.clearOptions();
    for (const option of this.toTomOptions()) {
      this.tom.addOption(option);
    }
    this.tom.refreshOptions(false);
    this.tom.setValue(this.value, true);
    this.onValidatorChange();
  }

  private toTomOptions(): Array<{ value: string; text: string }> {
    const options = this.taxOptions.map((tax) => ({
      value: tax.id,
      text: `[${tax.name}:${this.taxAmountLabel(tax)}]`,
    }));

    for (const taxId of this.missingTaxIds) {
      options.push({
        value: taxId,
        text: `[Unknown tax:${taxId}]`,
      });
    }

    return options;
  }

  private taxAmountLabel(tax: FinanceTaxRecord): string {
    if (tax.amount_type === 'percentage') {
      return `${tax.amount}%`;
    }

    if (tax.amount_type === 'division') {
      return `${tax.amount}% incl.`;
    }

    if (tax.amount_type === 'python_code') {
      return 'code';
    }

    return `${tax.amount}`;
  }

  private filterTaxes(taxes: FinanceTaxRecord[]): FinanceTaxRecord[] {
    return taxes
      .filter((tax) => tax.active !== false)
      .filter((tax) => this.scope === 'both' || tax.scope === this.scope || tax.scope === 'both');
  }

  private syncDisabledState(): void {
    if (!this.tom) {
      return;
    }

    if (this.disabled || this.internalDisabled) {
      this.tom.disable();
      return;
    }

    this.tom.enable();
  }

  private syncMissingTaxIds(): void {
    const knownTaxIds = new Set(this.taxOptions.map((tax) => tax.id));
    this.missingTaxIds = this.value.filter((taxId) => !knownTaxIds.has(taxId));
  }

  private onChange: (value: string[]) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
}
